/**
 * UTC parsing/formatting for scheduler timestamps.
 *
 * The scheduler serializes times via astropy's `Time.iso`, which yields a space-separated,
 * offset-less string (e.g. "2026-08-24 04:30:00.000"). Those instants are UTC, but the string
 * carries no timezone marker, so `new Date(str)` parses them as *local* time. Reading the result
 * back with the getUTC* accessors then adds the viewer's own offset on top of an already-correct
 * UTC value — a double offset. That is exactly what the multi-day timeline showed: +7h for a PDT
 * (UTC-7) viewer, i.e. 14h away from local instead of 7. `ensureUTC` pins the parse to UTC by
 * appending `Z` (and swapping the space for `T`) when no offset is present.
 */

// Trailing timezone marker: `Z`, `±HH:MM`, or `±HHMM`. Anchored to the end so the date part's own
// hyphens (YYYY-MM-DD) are never mistaken for an offset sign.
const HAS_TIMEZONE = /(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Normalize a server timestamp string to a form `new Date()` parses as UTC.
 * Non-strings (Date, number, null/undefined) pass through untouched for `new Date()` to handle.
 */
export function ensureUTC(str) {
  if (typeof str !== 'string') return str;
  if (HAS_TIMEZONE.test(str)) return str;
  return str.replace(' ', 'T') + 'Z';
}

/** Parse a server timestamp as UTC, returning a Date (Invalid Date if unparseable). */
export function parseUTC(dateStr) {
  return new Date(ensureUTC(dateStr));
}

/** Format a server timestamp as `HH:MM` in UTC. Empty string for missing/invalid input. */
export function formatUTCTime(dateStr) {
  if (!dateStr) return '';
  const date = parseUTC(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Format a server timestamp as `MM/DD HH:MM` in UTC. Empty string for missing/invalid input. */
export function formatUTCDateTime(dateStr) {
  if (!dateStr) return '';
  const date = parseUTC(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${month}/${day} ${hours}:${minutes}`;
}
